/**
 * Company Holiday Calendar Manager (LEAVE-REGISTRY-P1)
 * Admin / HR Manager: configure holidays excluded from leave day counts.
 * All other roles: read-only view (HOLIDAY-VISIBILITY-001).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CalendarDays, Plus, Trash2, Loader2, Eye, Star } from 'lucide-react';
import { toast } from 'sonner';
import { isHrReadOnly } from '../../lib/hrRole';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const HolidayCalendarManager = ({ readOnly: readOnlyProp = false }) => {
  const readOnly = readOnlyProp || isHrReadOnly();
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: '', name: '' });

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/leave-quota/holidays`, { params: { year } });
      setHolidays(res.data.holidays || []);
      setAvailableYears(res.data.available_years || []);
    } catch (err) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  // HOLIDAY-YEARS-002: dropdown for ALL roles = current year ∪ years that
  // actually have configured holidays. No rolling/hard-coded ranges. New
  // years appear automatically once HR adds the first holiday for them.
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([currentYear, ...availableYears])].sort((a, b) => a - b);

  useEffect(() => {
    if (!yearOptions.includes(year)) setYear(currentYear);
  }, [availableYears]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) return;
    try {
      setSaving(true);
      const addedYear = parseInt(form.date.slice(0, 4), 10);
      await axios.post(`${API}/api/leave-quota/holidays`, { date: form.date, name: form.name.trim() });
      toast.success('Holiday added');
      setForm({ date: '', name: '' });
      // Jump to the new holiday's year so it (and its new dropdown entry) is visible
      if (addedYear && addedYear !== year) setYear(addedYear); else fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/leave-quota/holidays/${id}`);
      toast.success('Holiday removed');
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove holiday');
    }
  };

  // HOLIDAY-UI-001: earliest→latest, next upcoming highlighted, past muted.
  const todayIso = new Date().toISOString().slice(0, 10);
  const sorted = [...holidays].sort((a, b) => a.date.localeCompare(b.date));
  const nextHolidayDate = sorted.find(h => h.date >= todayIso)?.date;

  const weekday = (iso) =>
    // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional weekday label
    new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div className="space-y-5" data-testid="holiday-calendar-manager">
      {/* Summary header + year selector */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-5 h-5 text-violet-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900" data-testid="holidays-summary-title">
              {year} Company Holidays
            </h3>
            {readOnly && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-500 text-[11px] font-medium"
                data-testid="holidays-view-only-badge"
              >
                <Eye className="w-3 h-3" />
                View only
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1 ml-[46px]" data-testid="holidays-summary-count">
            {loading ? 'Loading…' : `${sorted.length} holiday${sorted.length === 1 ? '' : 's'} scheduled`}
            <span className="text-slate-400"> · excluded from leave day calculations (along with weekends)</span>
          </p>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          data-testid="holiday-year-select"
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white shadow-sm flex-shrink-0"
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {!readOnly && (
      <form onSubmit={handleAdd} className="flex gap-3 items-end bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
            required
            data-testid="holiday-date-input"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">Holiday Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Independence Day"
            required
            minLength={2}
            maxLength={100}
            data-testid="holiday-name-input"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="holiday-add-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add
        </button>
      </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 bg-slate-50/60 border border-dashed border-slate-200 rounded-xl" data-testid="holidays-empty">
          <CalendarDays className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No company holidays configured for {year}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="holidays-list">
          {sorted.map(h => {
            const isNext = h.date === nextHolidayDate;
            const isPast = h.date < todayIso;
            const [, m, d] = h.date.split('-');
            return (
              <div
                key={h.id}
                data-testid={`holiday-row-${h.date}`}
                className={`relative flex items-start gap-3 rounded-xl border p-3 transition-shadow ${
                  isNext
                    ? 'border-violet-300 bg-violet-50/60 shadow-sm ring-1 ring-violet-200'
                    : isPast
                      ? 'border-slate-200 bg-slate-50/50 opacity-60'
                      : 'border-slate-200 bg-white shadow-sm hover:shadow-md'
                }`}
              >
                {/* Date badge */}
                <div
                  className={`w-11 h-11 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
                    isNext ? 'bg-violet-600 text-white' : isPast ? 'bg-slate-200 text-slate-500' : 'bg-violet-100 text-violet-700'
                  }`}
                >
                  <span className="text-base font-bold leading-none">{d}</span>
                  <span className="text-[9px] font-semibold tracking-widest mt-0.5">{MONTHS_SHORT[parseInt(m, 10) - 1]}</span>
                </div>

                {/* Holiday info */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold leading-snug line-clamp-2 ${isPast ? 'text-slate-500' : 'text-slate-900'}`}>
                    {h.name}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{weekday(h.date)}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {isNext && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-semibold"
                        data-testid="next-holiday-badge"
                      >
                        <Star className="w-2.5 h-2.5 fill-current" />
                        Next Holiday
                      </span>
                    )}
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                      isPast ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}>
                      Company Holiday
                    </span>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    onClick={() => handleDelete(h.id)}
                    data-testid={`holiday-delete-${h.date}`}
                    className="absolute top-2 right-2 p-1 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-md"
                    title="Remove holiday"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HolidayCalendarManager;
